import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, fmtINR } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Users, ShoppingBag, ClipboardList, IndianRupee, TrendingUp, MapPin, Pill, TestTube2, HeartPulse, CheckCircle2, FileText, UserPlus } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Line, LineChart, PieChart, Pie, Cell, Legend } from "recharts";
import { ORDER_STATUS_LABELS } from "@/lib/api";

const CHART_COLORS = ["#134E3F", "#E27D60", "#475569", "#F59E0B", "#3B82F6", "#8B5CF6"];
const TOOLTIP_STYLE = { borderRadius: 12, border: "1px solid #E2E0D8" };
const REVENUE_TOOLTIP_FORMATTER = (v) => fmtINR(v);

function Stat({ label, value, icon: Icon, tid, accent }) {
  return (
    <Card className="p-6 card-hover border-border" data-testid={tid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="label-caps text-muted-foreground">{label}</div>
          <div className="font-heading text-3xl tracking-tight text-foreground mt-3">{value}</div>
        </div>
        <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${accent ? "bg-accent/15 text-accent" : "bg-secondary text-primary"}`}>
          <Icon size={18} />
        </div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const [overview, setOverview] = useState(null);
  const [trend, setTrend] = useState([]);
  const [topPins, setTopPins] = useState([]);

  const loadAll = async () => {
    const [o, t, tp] = await Promise.all([
      api.get("/stats/overview"),
      api.get("/stats/trend"),
      api.get("/stats/top-pins"),
    ]);
    setOverview(o.data);
    setTrend(t.data);
    setTopPins(tp.data);
  };

  useEffect(() => {
    loadAll();
    const iv = setInterval(loadAll, 30000);
    return () => clearInterval(iv);
  }, []);

  const seedDemo = async () => {
    await api.post("/seed", { confirm: "medishiv-seed" });
    loadAll();
  };

  const statusData = useMemo(
    () => (overview?.status_distribution || []).map((s) => ({
      name: ORDER_STATUS_LABELS[s.status] || s.status,
      value: s.count,
      key: s.status,
    })),
    [overview?.status_distribution]
  );

  if (!overview) {
    return <div className="p-8 label-caps text-primary">Loading data…</div>;
  }

  return (
    <div className="p-8 space-y-8">
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="label-caps text-accent mb-2">Live Overview</div>
          <h1 className="font-heading text-4xl md:text-5xl tracking-tight text-primary">
            The pulse of Medishiv operations.
          </h1>
          <p className="text-muted-foreground mt-3 max-w-2xl">
            Full body checkup program at a glance — enrollments in the queue, orders in motion, revenue landed today.
          </p>
        </div>
        <button
          data-testid="seed-demo-btn"
          onClick={seedDemo}
          className="shrink-0 text-xs label-caps px-4 py-2 border border-border rounded-full hover:bg-secondary transition-colors"
        >
          Seed demo data
        </button>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Revenue today" value={fmtINR(overview.revenue_today)} icon={IndianRupee} tid="stat-revenue-today" accent />
        <Stat label="Today's registrations" value={overview.registrations_today ?? 0} icon={UserPlus} tid="stat-registrations-today" />
        <Stat label="Pending approvals" value={overview.pending_enrollments} icon={TrendingUp} tid="stat-pending-enrollments" />
        <Stat label="Total members" value={overview.total_members} icon={Users} tid="stat-total-members" />

        <Stat label="Today's medicine orders" value={overview.medicine_today ?? 0} icon={Pill} tid="stat-medicine-today" />
        <Stat label="Pending medicine orders" value={overview.medicine_pending ?? 0} icon={Pill} tid="stat-medicine-pending" accent />
        <Stat label="Completed orders" value={overview.completed_orders ?? 0} icon={CheckCircle2} tid="stat-completed-orders" />
        <Stat label="Lab bookings" value={overview.lab_bookings ?? 0} icon={TestTube2} tid="stat-lab-bookings" />

        <Stat label="Pending reports" value={overview.pending_reports ?? 0} icon={FileText} tid="stat-pending-reports" />
        <Stat label="Members pending FBC" value={overview.members_pending_fbc ?? 0} icon={HeartPulse} tid="stat-members-pending-fbc" accent />
        <Stat label="Lifetime revenue" value={fmtINR(overview.revenue_total)} icon={IndianRupee} tid="stat-total-revenue" />
        <Stat label="Serviceable PINs" value={overview.active_pins} icon={MapPin} tid="stat-active-pins" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-6 lg:col-span-2 border-border">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="label-caps text-muted-foreground">14-day trend</div>
              <h3 className="font-heading text-xl text-foreground mt-1">Orders & Enrollments</h3>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E0D8" />
              <XAxis dataKey="date" stroke="#64748B" fontSize={12} />
              <YAxis stroke="#64748B" fontSize={12} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Legend />
              <Line type="monotone" dataKey="orders" stroke="#134E3F" strokeWidth={2.5} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="enrollments" stroke="#E27D60" strokeWidth={2.5} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-6 border-border">
          <div className="label-caps text-muted-foreground">Orders by status</div>
          <h3 className="font-heading text-xl text-foreground mt-1 mb-4">Pipeline mix</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={3}>
                {statusData.map((s, i) => <Cell key={s.key || s.name} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={TOOLTIP_STYLE} />
            </PieChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-2 gap-2 mt-2">
            {statusData.map((s, i) => (
              <div key={s.name} className="flex items-center gap-2 text-xs text-muted-foreground">
                <span className="h-2 w-2 rounded-full" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }}></span>
                <span className="truncate">{s.name}</span>
                <span className="ml-auto font-medium text-foreground">{s.value}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-6 border-border">
          <div className="label-caps text-muted-foreground">Top PIN codes by orders</div>
          <h3 className="font-heading text-xl text-foreground mt-1 mb-4">Demand heatmap</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={topPins}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E0D8" />
              <XAxis dataKey="pin" stroke="#64748B" fontSize={12} />
              <YAxis stroke="#64748B" fontSize={12} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Bar dataKey="count" fill="#134E3F" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-6 border-border">
          <div className="label-caps text-muted-foreground">14-day revenue</div>
          <h3 className="font-heading text-xl text-foreground mt-1 mb-4">Cash landed</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E0D8" />
              <XAxis dataKey="date" stroke="#64748B" fontSize={12} />
              <YAxis stroke="#64748B" fontSize={12} />
              <Tooltip formatter={REVENUE_TOOLTIP_FORMATTER} contentStyle={TOOLTIP_STYLE} />
              <Bar dataKey="revenue" fill="#E27D60" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </div>
  );
}
